import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ogImage } from '../../lib/ogImage';
import { Instagram, ChevronRight } from 'lucide-react';
import BlogLayout from '../../components/BlogLayout';

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    { "@type": "Question", name: "Πόσο κοστίζει το πρώτο μου τατουάζ;", acceptedAnswer: { "@type": "Answer", text: "Εξαρτάται από το σχέδιο και το μέγεθος. Στείλε μας μήνυμα στο Instagram για προσφορά." } },
    { "@type": "Question", name: "Πόσο διαρκεί ένα πρώτο τατουάζ;", acceptedAnswer: { "@type": "Answer", text: "Συνήθως 30-90 λεπτά ανάλογα με το σχέδιο." } },
    { "@type": "Question", name: "Πώς κλείνω ραντεβού;", acceptedAnswer: { "@type": "Answer", text: "Μόνο μέσω Instagram: https://ig.me/m/210tattoo" } },
    { "@type": "Question", name: "Πονάει το πρώτο τατουάζ;", acceptedAnswer: { "@type": "Answer", text: "Ο πόνος εξαρτάται από τη θέση. Περιοχές με λιπώδη ιστό όπως ο μηρός και ο βραχίονας είναι πιο ανεκτικές. Πλευρά, λαιμός και αστράγαλος είναι πιο ευαίσθητα. Οι τεχνικές μας στοχεύουν να κάνουν τη διαδικασία όσο πιο άνετη γίνεται." } },
    { "@type": "Question", name: "Μπορώ να φέρω ιδέες για σχέδιο;", acceptedAnswer: { "@type": "Answer", text: "Ναι! Φέρτε εικόνες ή σκίτσα και θα τα προσαρμόσουμε." } },
    { "@type": "Question", name: "Τι πρέπει να κάνω πριν τη συνεδρία;", acceptedAnswer: { "@type": "Answer", text: "Φάε κανονικά πριν έρθεις, ενυδατώσου, φόρεσε άνετα ρούχα που επιτρέπουν πρόσβαση στην περιοχή και απόφυγε αλκοόλ 24 ώρες πριν." } },
  ],
};

export default function FirstTattooAthens() {
  useEffect(() => {
    const inject = (id: string, s: object) => {
      let tag = document.getElementById(id);
      if (!tag) { tag = document.createElement('script'); tag.id = id; (tag as HTMLScriptElement).type = 'application/ld+json'; document.head.appendChild(tag); }
      tag.textContent = JSON.stringify(s);
    };
    const articleSchema = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": "Το Πρώτο σου Τατουάζ στην Αθήνα",
      "description": "Κάνε το πρώτο σου τατουάζ στην Αθήνα με ασφάλεια και επαγγελματισμό. Το 210 Tattoo Athens προσφέρει custom σχέδια, εμπειρία 8+ ετών και φιλική εξυπηρέτηση.",
      "image": "https://210tattoo.gr/og-image.png",
      "datePublished": "2026-03-17",
      "dateModified": "2026-03-17",
      "author": { "@type": "Organization", "name": "210 Tattoo Athens", "url": "https://210tattoo.gr" },
      "publisher": { "@id": "https://210tattoo.gr/#tattooparlor" },
      "url": "https://210tattoo.gr/blog/first-tattoo-athens",
      "inLanguage": "el"
    };
    inject('schema-first-tattoo-faq', faqSchema);
    inject('schema-first-tattoo-article', articleSchema);
    return () => {
      document.getElementById('schema-first-tattoo-faq')?.remove();
      document.getElementById('schema-first-tattoo-article')?.remove();
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Το Πρώτο σου Τατουάζ στην Αθήνα | 210 Tattoo Athens</title>
        <meta name="description" content="Κάνε το πρώτο σου τατουάζ στην Αθήνα με ασφάλεια και επαγγελματισμό. Το 210 Tattoo Athens προσφέρει custom σχέδια, εμπειρία 8+ ετών και φιλική εξυπηρέτηση." />
        <link rel="canonical" href="https://210tattoo.gr/blog/first-tattoo-athens" />
        <link rel="alternate" hrefLang="el" href="https://210tattoo.gr/blog/first-tattoo-athens" />
        <link rel="alternate" hrefLang="x-default" href="https://210tattoo.gr/blog/first-tattoo-athens" />
        <meta property="og:title" content="Το Πρώτο σου Τατουάζ στην Αθήνα | 210 Tattoo Athens" />
        <meta property="og:description" content="Κάνε το πρώτο σου τατουάζ στην Αθήνα με ασφάλεια και επαγγελματισμό. Το 210 Tattoo Athens προσφέρει custom σχέδια, εμπειρία 8+ ετών και φιλική εξυπηρέτηση." />
        <meta property="og:url" content="https://210tattoo.gr/blog/first-tattoo-athens" />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={ogImage('first-tattoo-athens')} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Το Πρώτο σου Τατουάζ στην Αθήνα | 210 Tattoo Athens" />
        <meta name="twitter:description" content="Κάνε το πρώτο σου τατουάζ στην Αθήνα με ασφάλεια και επαγγελματισμό. Custom σχέδια, εμπειρία 8+ ετών." />
        <meta name="twitter:image" content={ogImage('first-tattoo-athens')} />
      </Helmet>

      <BlogLayout>
        {/* Hero */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-pink-950/20 via-black to-black pointer-events-none" />
          <div className="max-w-4xl mx-auto relative z-10">
            <div className="flex items-center gap-2 text-pink-500 text-sm font-medium mb-6">
              <a href="/" className="hover:text-pink-400 transition-colors">Home</a>
              <ChevronRight size={14} />
              <span>Πρώτο Τατουάζ Αθήνα</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              <span className="bg-gradient-to-r from-pink-500 via-pink-400 to-pink-300 bg-clip-text text-transparent">Το πρώτο σου τατουάζ</span>
              <br />
              <span className="text-white">στην Αθήνα</span>
            </h1>
            <p className="text-gray-500 text-xs mb-4 tracking-widest uppercase">17 Μαρτίου 2026</p>
            <p className="text-xl text-gray-300 mb-10 leading-relaxed max-w-2xl">
              Σκέφτεσαι να κάνεις το πρώτο σου τατουάζ στην Αθήνα; Στο 210 Tattoo Athens προσφέρουμε μια επαγγελματική και ασφαλή εμπειρία για κάθε αρχάριο. Με περισσότερα από 8 χρόνια εμπειρίας στην πόλη, γνωρίζουμε πόσο σημαντικό είναι να νιώθεις άνετα και σίγουρος για το πρώτο σου σχέδιο.
            </p>
            <a href="https://ig.me/m/210tattoo" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 inline-flex items-center gap-3 shadow-2xl hover:shadow-pink-500/30 transform hover:scale-105">
              <Instagram size={20} />
              Κλείσε Ραντεβού
            </a>
          </div>
        </section>

        {/* Intro */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Τι να <span className="text-pink-500">Περιμένεις</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-6">
              Το πρώτο τατουάζ φέρνει συχνά αγωνία, και αυτό είναι απολύτως φυσιολογικό. Ο περισσότερος κόσμος δεν ξέρει τι να αναμένει, πώς είναι η διαδικασία, πόσο πονάει και τι πρέπει να κάνει μετά. Σε αυτό το άρθρο καλύπτουμε όλα αυτά ώστε να έρθεις στη συνεδρία σου προετοιμασμένος.
            </p>
            <p className="text-gray-300 text-lg leading-relaxed mb-6">
              Η εμπειρία στο studio μας στο Γκύζη ξεκινάει με ένα consultation. Πριν από οτιδήποτε άλλο, μιλάμε μαζί σου για το τι θες, πού θες να το βάλεις, και αν έχεις ήδη ιδέες ή χρειάζεσαι βοήθεια με το σχέδιο. Δεν υπάρχει βιασύνη σε αυτή τη φάση.
            </p>
            <p className="text-gray-300 text-lg leading-relaxed">
              Εξυπηρετούμε πελάτες από Γκύζη, Κολωνάκι, Παγκράτι, Εξάρχεια, Αμπελόκηπους, Ζωγράφου, Χαλάνδρι, Γαλάτσι και από άλλες γειτονιές και προάστια της Αθήνας. Ο στόχος μας είναι να έχεις τέλεια πρώτη εμπειρία ανεξάρτητα από το πόσο κοντά ή μακριά μένεις.
            </p>
          </div>
        </section>

        {/* Choosing design */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Πώς να Επιλέξεις <span className="text-pink-500">Σχέδιο και Θέση</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-6">
              Δύο από τις πιο κοινές ερωτήσεις για το πρώτο τατουάζ είναι "τι να κάνω;" και "πού να το βάλω;". Οι απαντήσεις σε αυτές τις δύο ερωτήσεις συνδέονται.
            </p>
            <p className="text-gray-300 text-lg leading-relaxed mb-6">
              Για το σχέδιο, ξεκίνα από κάτι που έχεις σκεφτεί για αρκετό καιρό, όχι κάτι παρορμητικό. Το πρώτο τατουάζ τείνει να έχει μεγάλη συναισθηματική αξία για τα περισσότερα άτομα, οπότε αξίζει να αφιερώσεις χρόνο. Φέρε μαζί σου reference εικόνες, screenshots ή ακόμα και σκίτσα σε χαρτί. Ακόμα και μια γενική ιδέα βοηθά τον καλλιτέχνη να προτείνει τη σωστή κατεύθυνση.
            </p>
            <p className="text-gray-300 text-lg leading-relaxed mb-6">
              Για τη θέση, λάβε υπόψη μερικά πράγματα: τον πόνο, την ορατότητα, και το πώς θα γεράσει. Για αρχάριους συνιστάται συνήθως βραχίονας, αντιβράχιο, μηρός ή ωμοπλάτη, καθώς έχουν μεγαλύτερη επιφάνεια και λιγότερο άγχος. Αποφύγε για το πρώτο πλευρά, αυχένα και δάχτυλα αν δεν έχεις υψηλή ανοχή στον πόνο.
            </p>
            <p className="text-gray-300 text-lg leading-relaxed">
              Σχεδίαζε επίσης πού θα σε βλέπουν στη δουλειά σου. Εάν το εργασιακό σου περιβάλλον είναι αυστηρό, θέσεις που καλύπτονται από ρούχα είναι πιο πρακτικές.
            </p>
          </div>
        </section>

        {/* Pain guide */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Πονάει; <span className="text-pink-500">Οδηγός Πόνου ανά Περιοχή</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Ο πόνος είναι σχετικός και εξαρτάται από το άτομο, αλλά υπάρχουν γενικές κατηγορίες που ισχύουν για τους περισσότερους.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              {[
                { level: 'Χαμηλός', color: 'text-green-400', areas: 'Βραχίονας, μηρός, ωμοπλάτη, γλουτός', desc: 'Λιπώδης ιστός και μυς απορροφούν τη βελόνα. Καλή επιλογή για πρώτο τατουάζ.' },
                { level: 'Μέτριος', color: 'text-yellow-400', areas: 'Αντιβράχιο, γάμπα, στήθος', desc: 'Αντέχεται άνετα από τους περισσότερους. Μπορεί να υπάρξει κάποια ένταση στο τέλος της συνεδρίας.' },
                { level: 'Υψηλός', color: 'text-red-400', areas: 'Πλευρά, αυχένας, αστράγαλος, εσωτερικός βραχίονας, δάχτυλα', desc: 'Κόκαλα κοντά στην επιφάνεια ή πολλά νεύρα. Συνιστάται για δεύτερο ή τρίτο τατουάζ.' },
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-6 border border-pink-600/10">
                  <p className={`font-bold text-lg mb-2 ${item.color}`}>{item.level}</p>
                  <p className="text-white text-sm font-medium mb-2">{item.areas}</p>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
            <p className="text-gray-300 text-lg leading-relaxed">
              Το ίδιο σχέδιο σε διαφορετικές θέσεις μπορεί να δώσει εντελώς διαφορετική εμπειρία. Αν ανησυχείς για τον πόνο, μίλησέ μας και θα σου προτείνουμε θέση που ταιριάζει στο σχέδιό σου και είναι άνετη για εσένα.
            </p>
          </div>
        </section>

        {/* Before session */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Πριν τη Συνεδρία: <span className="text-pink-500">Checklist Προετοιμασίας</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Καλή προετοιμασία σημαίνει καλύτερο αποτέλεσμα και πιο άνετη εμπειρία. Αυτά είναι τα βασικά:
            </p>
            <div className="space-y-4 mb-6">
              {[
                { title: 'Φάε κανονικό γεύμα', desc: 'Μην πας νηστικός. Το σώμα σου χρειάζεται ενέργεια κατά τη διάρκεια της συνεδρίας. Ζάλη ή αδυναμία είναι πολύ πιο πιθανά αν δεν έχεις φάει.' },
                { title: 'Ενυδάτωσε καλά', desc: 'Τις μέρες πριν από τη συνεδρία, πίνε αρκετό νερό. Ενυδατωμένο δέρμα δέχεται πιο ομαλά τη βελόνα και επουλώνεται πιο γρήγορα.' },
                { title: 'Απόφυγε αλκοόλ 24 ώρες πριν', desc: 'Το αλκοόλ αραιώνει το αίμα και κάνει τη διαδικασία πιο δύσκολη για τον καλλιτέχνη. Μπορεί επίσης να επηρεάσει την επούλωση.' },
                { title: 'Φόρεσε κατάλληλα ρούχα', desc: 'Ρούχα που επιτρέπουν εύκολη πρόσβαση στη θέση του τατουάζ. Για βραχίονα φόρεσε μπλούζα με κοντά μανίκια ή αμάνικη. Για μηρό, παντελόνι που ανεβαίνει.' },
                { title: 'Ξύρισε αν χρειάζεται', desc: 'Αν η περιοχή έχει τρίχες, θα τη ξυρίσουμε εμείς ειδικά. Δεν χρειάζεται να το κάνεις μόνος σου.' },
                { title: 'Φέρε τις ιδέες σου', desc: 'Screenshots, εικόνες, σκίτσα, ό,τι έχεις. Ακόμα και αν δεν έχεις συγκεκριμένο σχέδιο, φέρε κατεύθυνση.' },
              ].map((item, i) => (
                <div key={i} className="flex gap-4 items-start">
                  <span className="w-2 h-2 rounded-full bg-pink-500 shrink-0 mt-2" />
                  <div>
                    <p className="text-white font-semibold mb-1">{item.title}</p>
                    <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* During session */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Κατά τη <span className="text-pink-500">Διάρκεια της Συνεδρίας</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-6">
              Μόλις ξεκινήσει η συνεδρία, το σώμα συνήθως χαλαρώνει μετά από λίγα πρώτα λεπτά. Η αρχή είναι συχνά η πιο δύσκολη ψυχολογικά, όχι πάντα η πιο επώδυνη.
            </p>
            <div className="space-y-6">
              {[
                { step: '01', title: 'Stencil και τοποθέτηση', desc: 'Πριν ξεκινήσουμε, μεταφέρουμε το σχέδιο στο δέρμα με stencil. Θα δεις πώς θα φαίνεται και μπορείς να ζητήσεις αλλαγές στη θέση ή το μέγεθος. Μόλις εγκρίνεις, ξεκινάμε.' },
                { step: '02', title: 'Κατά τη διάρκεια', desc: 'Μπορείς να μιλάς, να ακούς μουσική ή να χαλαρώσεις. Αν νιώσεις ζαλάδα ή υπερβολική ένταση, πες το αμέσως. Κάνουμε διαλείμματα όποτε χρειάζεται.' },
                { step: '03', title: 'Τελειώνοντας', desc: 'Στο τέλος της συνεδρίας καθαρίζουμε την περιοχή και τη σκεπάζουμε με προστατευτικό wrap ή foil. Σου δίνουμε πλήρεις οδηγίες για το aftercare.' },
              ].map((item) => (
                <div key={item.step} className="flex gap-6 items-start">
                  <div className="text-4xl font-bold text-pink-600/30 shrink-0 w-12 text-center leading-none pt-1">{item.step}</div>
                  <div className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 flex-1">
                    <h3 className="text-white font-semibold text-lg mb-2">{item.title}</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* After session */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Μετά τη Συνεδρία: <span className="text-pink-500">Βασικά για την Επούλωση</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-6">
              Η επούλωση είναι εξίσου σημαντική με τη συνεδρία. Τα πρώτα 2-3 εβδομάδες καθορίζουν πώς θα φαίνεται το τατουάζ σου μακροπρόθεσμα.
            </p>
            <div className="space-y-4 mb-6">
              {[
                { title: 'Πρώτες 24 ώρες', desc: 'Κρατάς το wrap ή foil για 2-4 ώρες. Μετά καθαρίζεις απαλά με ήπιο σαπούνι, στεγνώνεις σπογγίζοντας (όχι τρίβοντας) και αφήνεις να αναπνεύσει.' },
                { title: 'Εβδομάδα 1', desc: 'Καθαρισμός 2-3 φορές την ημέρα και ενυδάτωση με λεπτή στρώση αλοιφής. Αποφύγε κολύμπι, σόλαρ, άμεσο ήλιο και τρίψιμο.' },
                { title: 'Εβδομάδα 2-3', desc: 'Αρχίζει ξεφλούδισμα. Μην το ξύσεις. Συνέχισε ενυδάτωση. Το χρώμα μπορεί να φαίνεται θαμπό εδώ αλλά είναι φυσιολογικό.' },
                { title: 'Μακροπρόθεσμα', desc: 'SPF50 κάθε φορά που βγαίνεις με τον ήλιο είναι το πιο σημαντικό πράγμα για να κρατήσεις το τατουάζ σου φρέσκο.' },
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-6 border border-pink-600/10">
                  <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
            <p className="text-gray-300 text-lg leading-relaxed">
              Για αναλυτικές οδηγίες aftercare δες το άρθρο μας <Link to="/blog/tattoo-aftercare-athens" className="text-pink-400 hover:text-pink-300 underline">Aftercare Τατουάζ στην Αθήνα</Link>.
            </p>
          </div>
        </section>

        {/* Common mistakes */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Λάθη που Κάνουν <span className="text-pink-500">οι Πρωτάρηδες</span></h2>
            <div className="space-y-4">
              {[
                { title: 'Παρορμητική επιλογή σχεδίου', desc: 'Ένα τατουάζ μένει για πάντα. Άσε χρόνο να αποφασίσεις. Αν μια ιδέα είναι καλή σήμερα, θα είναι καλή και σε τρεις μήνες.' },
                { title: 'Επιλογή studio βάσει τιμής', desc: 'Φτηνό τατουάζ συχνά σημαίνει κομπρομίσα σε υγιεινή ή ποιότητα. Επένδυσε σωστά από την αρχή.' },
                { title: 'Παρουσία με αλκοόλ', desc: 'Μερικοί πιστεύουν ότι το αλκοόλ βοηθά στον πόνο. Το αντίθετο. Κάνει τη δουλειά πιο δύσκολη για τον καλλιτέχνη και την επούλωση πιο αργή.' },
                { title: 'Αμέλεια στο aftercare', desc: 'Ακόμα και τέλειο τατουάζ μπορεί να καταστραφεί από κακό aftercare. Ακολούθα τις οδηγίες αυστηρά τις πρώτες εβδομάδες.' },
                { title: 'Έκθεση στον αθηναϊκό ήλιο νωρίς', desc: 'Το καλοκαίρι στην Αθήνα είναι σκληρό για φρέσκα τατουάζ. Αν κάνεις τατουάζ τον Ιούνιο, φρόντισε να καλύπτεις την περιοχή για τουλάχιστον 4 εβδομάδες.' },
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-6 border border-pink-600/10">
                  <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why choose us */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-10">Γιατί να επιλέξεις το <span className="text-pink-500">210 Tattoo Athens</span> για το πρώτο σου τατουάζ;</h2>
            <div className="space-y-4">
              {[
                { title: 'Εμπειρία και ασφάλεια', desc: 'Από το 2018 κάνουμε τατουάζ στην Αθήνα. Οι καλλιτέχνες μας ακολουθούν αυστηρά πρότυπα υγιεινής: αποστειρωμένα εργαλεία μιας χρήσης, καθαρό χώρο εργασίας, καμία συμβιβαστική απόφαση.' },
                { title: 'Προσωποποιημένα σχέδια', desc: 'Δημιουργούμε σχέδια αποκλειστικά για σένα, σύμφωνα με το στυλ, τις προτιμήσεις και τη θέση που επιλέγεις. Δεν έχουμε "έτοιμα" σχέδια flash που κάνουμε σε όλους.' },
                { title: 'Φιλικό περιβάλλον για αρχάριους', desc: 'Για κάθε πελάτη που κάνει το πρώτο του τατουάζ, φροντίζουμε να εξηγήσουμε κάθε βήμα, να αντιμετωπίσουμε τις απορίες και να κάνουμε τη διαδικασία χαλαρή.' },
                { title: 'Κεντρική τοποθεσία', desc: 'Κεντρική Αθήνα, με εύκολη πρόσβαση από Κολωνάκι, Νεάπολη, Εξάρχεια, Παγκράτι και κεντρικές γραμμές του μετρό.' },
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200">
                  <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-10">Συχνές <span className="text-pink-500">Ερωτήσεις</span></h2>
            <div className="space-y-4">
              {[
                { q: 'Πόσο κοστίζει το πρώτο μου τατουάζ;', a: 'Εξαρτάται από το σχέδιο και το μέγεθος. Μικρά τατουάζ ξεκινούν από ένα βασικό ποσό, ενώ μεγαλύτερες δουλειές τιμολογούνται ανά ώρα. Στείλε μήνυμα στο Instagram με την ιδέα σου για εκτίμηση.' },
                { q: 'Πόσο διαρκεί ένα πρώτο τατουάζ;', a: 'Συνήθως 30-90 λεπτά ανάλογα με το σχέδιο. Στα αυτά δεν υπολογίζεται ο χρόνος consultation και stencil, οπότε πρόβλεψε τουλάχιστον 2 ώρες για την πρώτη σου επίσκεψη.' },
                { q: 'Πώς κλείνω ραντεβού;', a: 'Στέλνεις μήνυμα στο Instagram @210tattoo. Δεν δεχόμαστε walk-in χωρίς ραντεβού.' },
                { q: 'Πονάει το πρώτο τατουάζ;', a: 'Ο πόνος εξαρτάται από τη θέση. Περιοχές με λιπώδη ιστό όπως ο μηρός και ο βραχίονας είναι πιο ανεκτικές. Πλευρά, λαιμός και αστράγαλος είναι πιο ευαίσθητα. Οι τεχνικές μας στοχεύουν να κάνουν τη διαδικασία όσο πιο άνετη γίνεται.' },
                { q: 'Μπορώ να φέρω ιδέες για σχέδιο;', a: 'Ναι, φέρε ό,τι έχεις. Εικόνες από Instagram, Pinterest, σκίτσα, ακόμα και λεκτική περιγραφή. Ο καλλιτέχνης θα δουλέψει από αυτά για να φτιάξει το σχέδιό σου.' },
                { q: 'Τι πρέπει να κάνω πριν τη συνεδρία;', a: 'Φάε κανονικά πριν έρθεις, ενυδατώσου, φόρεσε άνετα ρούχα που επιτρέπουν πρόσβαση στην περιοχή και απόφυγε αλκοόλ 24 ώρες πριν.' },
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-6 border border-pink-600/10">
                  <h3 className="text-white font-semibold mb-2">{item.q}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Έτοιμος για το <span className="text-pink-500">Πρώτο σου Τατουάζ;</span></h2>
            <p className="text-gray-300 text-lg mb-8">
              Στείλε μήνυμα στο Instagram ή κάλεσε στο <strong className="text-white">694 066 4049</strong>. Δεχόμαστε πελάτες από όλη την Αθήνα και χαιρόμαστε ιδιαίτερα όταν κάποιος κάνει το πρώτο του τατουάζ μαζί μας.
            </p>
            <a href="https://ig.me/m/210tattoo" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 inline-flex items-center gap-3 shadow-2xl hover:shadow-pink-500/30 transform hover:scale-105">
              <Instagram size={20} />
              Κλείσε το πρώτο σου ραντεβού τώρα
            </a>
          </div>
        </section>

        {/* Internal links */}

        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-8">Σχετικά <span className="text-pink-500">Άρθρα</span></h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Link to="/blog/tattoo-aftercare-athens" className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200 group">
                <p className="text-pink-500 text-xs uppercase tracking-widest mb-2">Άρθρο</p>
                <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">Aftercare Τατουάζ στην Αθήνα</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">Ό,τι χρειάζεσαι να ξέρεις για τις πρώτες εβδομάδες μετά τη συνεδρία.</p>
              </Link>
              <Link to="/blog/tattoo-styles-athens" className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200 group">
                <p className="text-pink-500 text-xs uppercase tracking-widest mb-2">Άρθρο</p>
                <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">Στυλ Τατουάζ στην Αθήνα</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">Realism, fine line, traditional, geometric: βρες το στυλ που σε εκφράζει.</p>
              </Link>
              <Link to="/blog/lathi-proto-tatouaz-athina" className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200 group">
                <p className="text-pink-500 text-xs uppercase tracking-widest mb-2">Άρθρο</p>
                <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">7 Λάθη στο Πρώτο σου Τατουάζ</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">Τα πιο συχνά λάθη των αρχάριων και πώς να τα αποφύγεις.</p>
              </Link>
            </div>
          </div>
        </section>
        <section className="py-16 bg-gray-950 border-t border-pink-600/10">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-8 text-center">Υπηρεσίες <span className="text-pink-500">/ Services</span></h2>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="/fine-line-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Fine Line Tattoo Athens</a>
              <a href="/realistic-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Realistic Tattoo Athens</a>
              <a href="/cover-up-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Cover Up Tattoo Athens</a>
              <a href="/black-gray-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Black &amp; Gray Tattoo Athens</a>
            </div>
          </div>
        </section>
      </BlogLayout>
    </>
  );
}
